import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { api } from '../../utils/api'
import toast from 'react-hot-toast'

const MemberScrollEditor = () => {
  const navigate = useNavigate()
  const { user } = useAuth()

  const [memberList, setMemberList] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingMembers, setEditingMembers] = useState({})
  const [savingMembers, setSavingMembers] = useState({})
  const [bulkInput, setBulkInput] = useState('')
  const [showBulkInput, setShowBulkInput] = useState(false)
  const [newMemberName, setNewMemberName] = useState('')
  const [showAddMemberInput, setShowAddMemberInput] = useState(false)
  const [selectedMembers, setSelectedMembers] = useState(new Set())
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false)
  const [syncLoading, setSyncLoading] = useState(false)
  const [accessToken, setAccessToken] = useState('')
  const [showSyncInput, setShowSyncInput] = useState(false)
  const [isYouTubeConnected, setIsYouTubeConnected] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)

  useEffect(() => {
    fetchMemberList()
    checkYouTubeStatus()

    // Check if we just returned from OAuth
    const params = new URLSearchParams(window.location.search)
    if (params.get('youtube_connected') === 'true') {
      toast.success('YouTube account connected successfully!')
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  }, [])

  const checkYouTubeStatus = async () => {
    try {
      const response = await api.get('/auth/youtube/status')
      setIsYouTubeConnected(response.data.connected)
    } catch (error) {
      console.error('Failed to check YouTube status:', error)
    }
  }

  const fetchMemberList = async () => {
    try {
      const response = await api.get('/member-scroll')
      setMemberList(response.data.members || [])
    } catch (error) {
      console.error('Failed to fetch member list:', error)
      toast.error('Failed to load member list')
    } finally {
      setLoading(false)
    }
  }

  const toggleEdit = (memberId) => {
    setEditingMembers(prev => ({
      ...prev,
      [memberId]: !prev[memberId]
    }))
  }

  const isEditing = (memberId) => {
    return editingMembers[memberId] || false
  }

  const isSaving = (memberId) => {
    return savingMembers[memberId] || false
  }

  const updateMemberLocally = (memberId, updates) => {
    setMemberList(prev => prev.map(member =>
      member.id === memberId ? { ...member, ...updates } : member
    ))
  }

  const saveMember = async (memberId) => {
    setSavingMembers(prev => ({ ...prev, [memberId]: true }))

    try {
      const member = memberList.find(m => m.id === memberId)
      if (!member) {
        throw new Error('Member not found')
      }

      const updates = {
        name: member.name
      }

      console.log('Sending update to backend:', updates)
      console.log('Member data before update:', member)

      await api.put(`/member-scroll/${memberId}`, updates)

      // Exit edit mode
      setEditingMembers(prev => ({ ...prev, [memberId]: false }))

      toast.success('Member saved successfully')
    } catch (error) {
      console.error('Failed to save member:', error)
      toast.error('Failed to save member')
    } finally {
      setSavingMembers(prev => ({ ...prev, [memberId]: false }))
    }
  }

  const deleteMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to delete this member?')) {
      return
    }

    try {
      await api.delete(`/member-scroll/${memberId}`)
      setMemberList(prev => prev.filter(member => member.id !== memberId))
      toast.success('Member deleted successfully')
    } catch (error) {
      console.error('Failed to delete member:', error)
      toast.error('Failed to delete member')
    }
  }

  const handleAddMember = async () => {
    if (!newMemberName.trim()) {
      toast.error('Please enter a member name')
      return
    }

    const tempId = `temp-${Date.now()}`

    try {
      // Add new member locally first
      const newMember = {
        id: tempId,
        name: newMemberName.trim(),
        isActive: true,
        sortOrder: memberList.length
      }

      setMemberList(prev => [...prev, newMember])

      // Save to server
      const response = await api.post('/member-scroll', { name: newMemberName.trim() })

      // Update with server response
      setMemberList(prev => prev.map(m =>
        m.id === tempId ? response.data.member : m
      ))

      setNewMemberName('')
      setShowAddMemberInput(false)
      toast.success('Member added successfully')
    } catch (error) {
      console.error('Failed to add member:', error)
      toast.error('Failed to add member')
      // Remove the temporary member if server save failed
      setMemberList(prev => prev.filter(m => m.id !== tempId))
    }
  }

  const handleBulkImport = async () => {
    if (!bulkInput.trim()) {
      toast.error('Please enter member names')
      return
    }

    try {
      // Parse the input - support both comma-separated and line-separated
      const names = bulkInput
        .split(/[,\n]/)
        .map(name => name.trim())
        .filter(name => name.length > 0)

      if (names.length === 0) {
        toast.error('No valid names found')
        return
      }

      const response = await api.post('/member-scroll/bulk', { names })
      setMemberList(response.data.members || [])
      setBulkInput('')
      setShowBulkInput(false)
      toast.success(`${names.length} members added successfully`)
    } catch (error) {
      console.error('Failed to import members:', error)
      toast.error('Failed to import members')
    }
  }

  const toggleMemberActive = async (memberId) => {
    try {
      const member = memberList.find(m => m.id === memberId)
      if (!member) return

      const response = await api.put(`/member-scroll/${memberId}`, {
        isActive: !member.isActive
      })

      updateMemberLocally(memberId, { isActive: !member.isActive })
      toast.success('Member status updated')
    } catch (error) {
      console.error('Failed to update member status:', error)
      toast.error('Failed to update member status')
    }
  }

  const toggleMemberSelection = (memberId) => {
    setSelectedMembers(prev => {
      const newSet = new Set(prev)
      if (newSet.has(memberId)) {
        newSet.delete(memberId)
      } else {
        newSet.add(memberId)
      }
      return newSet
    })
  }

  const selectAllMembers = () => {
    setSelectedMembers(new Set(memberList.map(member => member.id)))
  }

  const clearSelection = () => {
    setSelectedMembers(new Set())
  }

  const handleBulkDelete = async () => {
    if (selectedMembers.size === 0) {
      toast.error('Please select members to delete')
      return
    }

    const confirmMessage = `Are you sure you want to delete ${selectedMembers.size} member${selectedMembers.size > 1 ? 's' : ''}? This action cannot be undone.`
    if (!window.confirm(confirmMessage)) {
      return
    }

    setBulkDeleteLoading(true)

    try {
      const ids = Array.from(selectedMembers)
      console.log('Deleting members with IDs:', ids)

      // Use POST method for bulk delete (some servers don't handle DELETE with body well)
      const response = await api.post('/member-scroll/bulk-delete', { ids })

      console.log('Bulk delete response:', response.data)

      // Remove deleted members from local state
      setMemberList(prev => prev.filter(member => !selectedMembers.has(member.id)))

      // Clear selection
      setSelectedMembers(new Set())

      toast.success(`${ids.length} member${ids.length > 1 ? 's' : ''} deleted successfully`)
    } catch (error) {
      console.error('Failed to delete members:', error)
      console.error('Error details:', error.response?.data || error.message)
      const errorMessage = error.response?.data?.error || error.message || 'Failed to delete members'
      toast.error(`Delete failed: ${errorMessage}`)
    } finally {
      setBulkDeleteLoading(false)
    }
  }

  const handleYoutubeConnect = async () => {
    try {
      const response = await api.get('/auth/youtube/connect')
      if (response.data.url) {
        window.location.href = response.data.url
      }
    } catch (error) {
      console.error('Failed to get YouTube auth URL:', error)
      toast.error('Failed to initiate YouTube connection')
    }
  }

  const handleYoutubeDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect your YouTube account?')) return

    setIsDisconnecting(true)
    try {
      await api.delete('/auth/youtube/disconnect')
      setIsYouTubeConnected(false)
      toast.success('YouTube account disconnected')
    } catch (error) {
      console.error('Failed to disconnect YouTube:', error)
      toast.error('Failed to disconnect YouTube')
    } finally {
      setIsDisconnecting(false)
    }
  }

  const handleYoutubeSync = async () => {
    if (!isYouTubeConnected && !accessToken.trim()) {
      toast.error('Please connect your YouTube account or enter an Access Token')
      return
    }

    setSyncLoading(true)
    try {
      // If connected, we don't need to pass accessToken (backend will use stored one)
      const data = isYouTubeConnected ? {} : { accessToken: accessToken.trim() }
      const response = await api.post('/member-scroll/sync-youtube', data)
      toast.success(response.data.message)
      fetchMemberList() // Refresh the list
      setAccessToken('')
      setShowSyncInput(false)
    } catch (error) {
      console.error('YouTube sync failed:', error)
      const message = error.response?.data?.message || 'Failed to sync with YouTube'
      toast.error(message)
      if (message.includes('Authorization') && !isYouTubeConnected) {
        window.open('https://developers.google.com/oauthplayground/', '_blank')
      }
    } finally {
      setSyncLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Loading member list...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-black text-white px-6 pb-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/overlay')}
          className="text-white hover:text-gray-300 transition-colors flex items-center gap-2 font-avenir mb-4"
        >
          <span>&lt;</span>
          BACK
        </button>
        <h1 className="text-2xl font-bold text-white">Member Scroll Editor</h1>
        <p className="text-gray-400 mt-2">Manage the list of members that will scroll across the bottom of your overlay</p>
      </div>

      {/* Bulk Import Section */}
      <div className="bg-[#1c1c1c] p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">Bulk Import</h2>
          <button
            onClick={() => setShowBulkInput(!showBulkInput)}
            className="bg-transparent border border-white text-white px-4 py-2 hover:bg-white hover:text-black transition-colors"
          >
            {showBulkInput ? 'Hide' : 'Show'} Bulk Import
          </button>
        </div>

        {showBulkInput && (
          <div className="space-y-4">
            <div>
              <label className="block text-white text-sm mb-2">
                Enter member names (one per line or comma-separated):
              </label>
              <textarea
                value={bulkInput}
                onChange={(e) => setBulkInput(e.target.value)}
                className="w-full h-32 bg-black border border-white text-white p-3 resize-none"
                placeholder="John Doe&#10;Jane Smith&#10;Bob Johnson&#10;or&#10;John Doe, Jane Smith, Bob Johnson"
              />
            </div>
            <div className="flex gap-4">
              <button
                onClick={handleBulkImport}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 transition-colors"
              >
                Import Members
              </button>
              <button
                onClick={() => {
                  setBulkInput('')
                  setShowBulkInput(false)
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* YouTube Synchronization Section */}
      {/* YouTube Synchronization Section */}
      <div className="bg-[#1c1c1c] p-6 mb-8 border border-red-900/30">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">YouTube Sync</h2>
              <div className="flex items-center gap-2 mt-1">
                <div className={`w-2 h-2 rounded-full ${isYouTubeConnected ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                <span className="text-xs text-gray-400">
                  {isYouTubeConnected ? 'YouTube Account Connected' : 'YouTube Account Not Linked'}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-3">
            {isYouTubeConnected ? (
              <>
                <button
                  onClick={handleYoutubeDisconnect}
                  disabled={isDisconnecting}
                  className="bg-transparent border border-gray-600 text-gray-400 px-4 py-2 hover:bg-gray-800 transition-colors flex items-center gap-2"
                >
                  {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
                </button>
                <button
                  onClick={handleYoutubeSync}
                  disabled={syncLoading}
                  className="bg-red-600 border border-red-600 text-white px-4 py-2 hover:bg-red-700 transition-colors flex items-center gap-2"
                >
                  {syncLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Syncing...
                    </>
                  ) : (
                    'Sync Members'
                  )}
                </button>
              </>
            ) : (
              <button
                onClick={handleYoutubeConnect}
                className="bg-red-600 border border-red-600 text-white px-4 py-2 hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                Connect YouTube Account
              </button>
            )}
            <button
              onClick={() => setShowSyncInput(!showSyncInput)}
              className="bg-transparent border border-white text-white px-4 py-2 hover:bg-white hover:text-black transition-colors"
            >
              {showSyncInput ? 'Hide' : 'Manual Sync'}
            </button>
          </div>
        </div>

        {showSyncInput && (
          <div className="space-y-4 pt-4 border-t border-gray-800">
            <div className="bg-red-900/20 border border-red-900/50 p-4 text-sm text-red-200">
              <p className="font-bold mb-1">Manual Access Token</p>
              <p>Use this if you don't want to link your account permanently. Requires <code className="bg-black/50 px-1">youtube.channel-memberships.creator</code> scope.</p>
              <a
                href="https://developers.google.com/oauthplayground/"
                target="_blank"
                rel="noreferrer"
                className="text-white underline mt-2 inline-block hover:text-red-300"
              >
                Get a temporary token from Google OAuth Playground &rarr;
              </a>
            </div>

            <div>
              <label className="block text-white text-sm mb-2">
                Enter YouTube Access Token:
              </label>
              <input
                type="password"
                value={accessToken}
                onChange={(e) => setAccessToken(e.target.value)}
                className="w-full bg-black border border-white text-white p-3 font-mono text-sm"
                placeholder="ya29.a0AfH6SM..."
              />
            </div>

            <div className="flex gap-4">
              <button
                onClick={handleYoutubeSync}
                disabled={syncLoading}
                className="bg-red-600 hover:bg-red-700 disabled:bg-red-800 text-white px-6 py-2 transition-colors flex items-center gap-2"
              >
                {syncLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Syncing...
                  </>
                ) : (
                  'Start Manual Sync'
                )}
              </button>
              <button
                onClick={() => {
                  setAccessToken('')
                  setShowSyncInput(false)
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Member List */}
      <div className="bg-[#1c1c1c] p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Member List ({memberList.length})</h2>
          <div className="flex items-center gap-4">
            {memberList.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={selectAllMembers}
                  className="bg-transparent border border-blue-500 text-blue-500 px-3 py-1 text-sm hover:bg-blue-500 hover:text-white transition-colors"
                >
                  Select All
                </button>
                <button
                  onClick={clearSelection}
                  className="bg-transparent border border-gray-500 text-gray-500 px-3 py-1 text-sm hover:bg-gray-500 hover:text-white transition-colors"
                >
                  Clear
                </button>
                <button
                  onClick={async () => {
                    if (memberList.length === 0) {
                      toast.error('No members to delete')
                      return
                    }

                    const confirmMessage = `Are you sure you want to delete ALL ${memberList.length} member${memberList.length > 1 ? 's' : ''}? This action cannot be undone.`
                    if (!window.confirm(confirmMessage)) {
                      return
                    }

                    setBulkDeleteLoading(true)
                    try {
                      const ids = memberList.map(member => member.id)
                      console.log('Deleting all members with IDs:', ids)

                      const response = await api.post('/member-scroll/bulk-delete', { ids })

                      console.log('Bulk delete all response:', response.data)

                      // Clear the entire member list
                      setMemberList([])
                      setSelectedMembers(new Set())

                      toast.success(`All ${ids.length} member${ids.length > 1 ? 's' : ''} deleted successfully`)
                    } catch (error) {
                      console.error('Failed to delete all members:', error)
                      console.error('Error details:', error.response?.data || error.message)
                      const errorMessage = error.response?.data?.error || error.message || 'Failed to delete all members'
                      toast.error(`Delete all failed: ${errorMessage}`)
                    } finally {
                      setBulkDeleteLoading(false)
                    }
                  }}
                  disabled={bulkDeleteLoading}
                  className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-3 py-1 text-sm transition-colors flex items-center gap-2"
                >
                  {bulkDeleteLoading ? (
                    <>
                      <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                      Deleting All...
                    </>
                  ) : (
                    'Delete All'
                  )}
                </button>
                {selectedMembers.size > 0 && (
                  <button
                    onClick={handleBulkDelete}
                    disabled={bulkDeleteLoading}
                    className="bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white px-4 py-1 text-sm flex items-center gap-2 transition-colors"
                  >
                    {bulkDeleteLoading ? (
                      <>
                        <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                        Deleting...
                      </>
                    ) : (
                      `Delete Selected (${selectedMembers.size})`
                    )}
                  </button>
                )}
              </div>
            )}
            <button
              onClick={() => setShowAddMemberInput(true)}
              className="bg-transparent border border-white text-white px-4 py-2 hover:bg-white hover:text-black transition-colors"
            >
              + Add Member
            </button>
          </div>
        </div>

        {/* Add Member Input Form */}
        {showAddMemberInput && (
          <div className="mb-6 p-4 bg-gray-800 rounded border border-gray-600">
            <div className="mb-4">
              <label className="block text-white text-sm mb-2">Add New Member:</label>
              <input
                type="text"
                value={newMemberName}
                onChange={(e) => setNewMemberName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleAddMember()
                  }
                }}
                placeholder="Enter member name..."
                className="w-full bg-black border border-gray-500 text-white px-3 py-2 text-sm rounded focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
                autoFocus
              />
            </div>
            <div className="flex gap-4">
              <button
                onClick={handleAddMember}
                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 transition-colors"
              >
                Add Member
              </button>
              <button
                onClick={() => {
                  setNewMemberName('')
                  setShowAddMemberInput(false)
                }}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {memberList.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-400 text-lg">No members added yet</p>
            <p className="text-gray-500 text-sm mt-2">Use bulk import or add individual members to get started</p>
          </div>
        ) : (
          <div className="space-y-3">
            {memberList
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map((member) => (
                <div key={member.id} className={`bg-[#323232] border border-white p-4 flex items-center justify-between ${!member.isActive ? 'opacity-50' : ''
                  } ${selectedMembers.has(member.id) ? 'ring-2 ring-blue-500' : ''}`}>
                  <div className="flex-1 flex items-center gap-4">
                    <input
                      type="checkbox"
                      checked={selectedMembers.has(member.id)}
                      onChange={() => toggleMemberSelection(member.id)}
                      className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                    />
                    <button
                      onClick={() => toggleMemberActive(member.id)}
                      className={`w-6 h-6 border-2 rounded transition-colors ${member.isActive
                        ? 'bg-green-600 border-green-600'
                        : 'bg-transparent border-gray-400'
                        }`}
                      title={member.isActive ? 'Active - click to deactivate' : 'Inactive - click to activate'}
                    >
                      {member.isActive && (
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>

                    <div className="flex-1">
                      {isEditing(member.id) ? (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={member.name}
                            onChange={(e) => updateMemberLocally(member.id, { name: e.target.value })}
                            className="w-full bg-transparent text-white text-lg font-medium border-b border-gray-500 outline-none"
                            placeholder="Member name"
                          />
                        </div>
                      ) : (
                        <div>
                          <div className="text-white text-lg font-medium">
                            {member.name}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {isEditing(member.id) ? (
                      <button
                        onClick={() => saveMember(member.id)}
                        disabled={isSaving(member.id)}
                        className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-3 py-1 text-sm flex items-center gap-1"
                      >
                        {isSaving(member.id) ? (
                          <>
                            <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                            Saving...
                          </>
                        ) : (
                          'SAVE'
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={() => toggleEdit(member.id)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 text-sm"
                      >
                        EDIT
                      </button>
                    )}
                    <button
                      onClick={() => deleteMember(member.id)}
                      className="text-white text-sm hover:text-gray-300 transition-colors"
                    >
                      DELETE
                    </button>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default MemberScrollEditor
